'use client';

import * as React from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

import { cn } from '@/lib/utils';

// No `type` or `scrollHideDelay`: Radix's own show/hide timing is replaced by the app's one scrollbar
// rule. `auto` keeps the bar mounted whenever the content overflows, and its thumb shows only while
// `lib/scrollActivity.ts` marks the viewport active or the bar is hovered (app/styles/base.css).
function ScrollArea({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof ScrollAreaPrimitive.Root>, 'type' | 'scrollHideDelay'>) {
  return (
    <ScrollAreaPrimitive.Root data-slot="scroll-area" type="auto" className={cn('relative', className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        // Radix gives this generated direct child `display: table; min-width: 100%` inline. That is useful
        // for two-axis areas, but a vertical rail then grows to its longest unbreakable label and clips the
        // numbers at the trailing edge. This primitive composes only a vertical scrollbar, so its content
        // owner is explicitly a block constrained to the viewport width.
        className="size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-1 [&>div]:!block [&>div]:!w-full [&>div]:!min-w-0"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Scrollbar>) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        'flex touch-none p-0.5 transition-colors select-none',
        orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent',
        orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb data-slot="scroll-area-thumb" className="relative flex-1 rounded-full" />
    </ScrollAreaPrimitive.Scrollbar>
  );
}

// `ScrollBar` stays internal, unlike stock shadcn. Every scroller in this app wants the vertical bar the
// `ScrollArea` already composes, so an exported second entry point would be an export nothing imports —
// which `knip` fails the build over, and rightly: a primitive part with no call site is a part no design
// decision stands behind. Export it the day a call site needs a horizontal bar of its own.
export { ScrollArea };
